import { Caption1, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useDataTableStyles } from "@/components/data-table";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import type { WeeklyTone } from "@/features/weekly-report/weekly-report-model";
import { formatDate } from "@/lib/date-format";
import { TrendLineVisual } from "./charts";
import { ReportVisual } from "./ReportVisual";
import type { VisualTable } from "./visual-table";

/**
 * 차트2의 주간 보고 표식.
 * 글과 숫자가 본문이고, 색은 주의·위험에만 쓴다. 지표 아래 게이지는 두지 않는다.
 */

const useStyles = makeStyles({
  quiet: { color: tokens.colorNeutralForeground3 },
  neutral: { color: tokens.colorNeutralForeground1 },
  good: { color: tokens.colorPaletteGreenForeground1 },
  watch: { color: tokens.colorPaletteDarkOrangeForeground1 },
  risk: { color: tokens.colorPaletteRedForeground1 },
  word: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  age: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  ageRow: {
    display: "grid",
    gridTemplateColumns: "72px minmax(0, 1fr) 36px",
    alignItems: "center",
    columnGap: "8px",
    minHeight: "28px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    ":first-child": { borderTop: "none" },
  },
  ageTrack: {
    position: "relative",
    height: "4px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground5,
    overflow: "hidden",
  },
  ageFill: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralForeground4,
  },
  ageCount: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  frame: {
    width: "100%",
    minWidth: 0,
    overflowX: "auto",
  },
  sheet: {
    width: "100%",
    minWidth: "720px",
    borderCollapse: "collapse",
  },
  cell: {
    height: "auto",
    minHeight: "34px",
  },
  person: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  clip: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sub: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  pair: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.5fr) minmax(220px, 0.7fr)",
    gap: tokens.spacingHorizontalL,
    minWidth: 0,
    "@container inkpair (max-width: 720px)": {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  pairWrap: {
    containerType: "inline-size",
    containerName: "inkpair",
    minWidth: 0,
  },
});

const TONE = {
  neutral: "neutral",
  quiet: "quiet",
  good: "good",
  watch: "watch",
  risk: "risk",
} as const;

export function StatusWord({ children, tone = "neutral" }: { children: string; tone?: WeeklyTone }) {
  const styles = useStyles();
  return <span className={mergeClasses(styles.word, styles[TONE[tone]])}>{children}</span>;
}

/** 계획 대비 차이. 앞서면 달성, 뒤지면 지연. 색은 그 단어에만 둔다. */
export function ProgressDelta({ gap }: { gap: number }) {
  const ahead = gap >= 0;
  const text = `${ahead ? "+" : ""}${gap.toFixed(1)}%p ${ahead ? "달성" : "지연"}`;
  return <StatusWord tone={ahead ? "good" : "watch"}>{text}</StatusWord>;
}

export interface InkAgingBand {
  key: string;
  label: string;
  count: number;
  tone: WeeklyTone;
}

export function AgingList({ bands }: { bands: readonly InkAgingBand[] }) {
  const styles = useStyles();
  const max = Math.max(1, ...bands.map((band) => band.count));
  return (
    <div className={styles.age} role="list" aria-label="미결 이슈 나이">
      {bands.map((band) => (
        <div key={band.key} className={styles.ageRow} role="listitem">
          <span className={styles.word}>{band.label}</span>
          <span className={styles.ageTrack} aria-hidden>
            <span className={styles.ageFill} style={{ width: `${Math.round((band.count / max) * 100)}%` }} />
          </span>
          <span className={mergeClasses(styles.ageCount, styles[TONE[band.tone]])}>{band.count}</span>
        </div>
      ))}
    </div>
  );
}

export interface InkVarianceRow {
  key: string;
  name: string;
  note?: string;
  owner: string;
  ownerRole?: string;
  plan: string;
  actual: string;
  actualKind?: string;
  slip: string;
  slipTone: WeeklyTone;
  status: string;
  statusTone: WeeklyTone;
}

export function VarianceTable({ rows, label }: { rows: readonly InkVarianceRow[]; label: string }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  return (
    <div className={styles.frame}>
      <table className={mergeClasses(table.table, styles.sheet)} aria-label={label}>
        <colgroup>
          <col style={{ width: "24%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "14%" }} />
        </colgroup>
        <thead>
          <tr>
            <th className={table.th} scope="col">
              일정
            </th>
            <th className={table.th} scope="col">
              담당
            </th>
            <th className={mergeClasses(table.th, table.num)} scope="col">
              계획
            </th>
            <th className={mergeClasses(table.th, table.num)} scope="col">
              실적·전망
            </th>
            <th className={mergeClasses(table.th, table.num)} scope="col">
              차이
            </th>
            <th className={table.th} scope="col">
              상태
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th className={mergeClasses(table.td, styles.cell)} scope="row">
                <Tooltip content={row.note ? `${row.name}. ${row.note}` : row.name} relationship="description">
                  <span className={styles.clip}>{row.name}</span>
                </Tooltip>
              </th>
              <td className={mergeClasses(table.td, styles.cell)}>
                <Owner name={row.owner} role={row.ownerRole} />
              </td>
              <td className={mergeClasses(table.td, table.num, styles.cell)}>{row.plan}</td>
              <td className={mergeClasses(table.td, table.num, styles.cell)}>
                {row.actualKind ? (
                  <span className={table.twoLine}>
                    <span className={table.numLabel}>{row.actual}</span>
                    <Caption1 className={mergeClasses(styles.sub, table.numLabel)}>{row.actualKind}</Caption1>
                  </span>
                ) : (
                  row.actual
                )}
              </td>
              <td className={mergeClasses(table.td, table.num, styles.cell)}>
                <span className={styles[TONE[row.slipTone]]}>{row.slip}</span>
              </td>
              <td className={mergeClasses(table.td, styles.cell)}>
                <StatusWord tone={row.statusTone}>{row.status}</StatusWord>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Owner({ name, role }: { name: string; role?: string }) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const tip = role ? `${name} · ${role}` : name;
  return (
    <Tooltip content={tip} relationship="description">
      <span className={styles.person}>
        <PersonAvatar name={name} size={20} tip={false} />
        <span className={mergeClasses(styles.clip, table.clip)}>{name}</span>
      </span>
    </Tooltip>
  );
}

export interface InkIssuePoint {
  weekId: string;
  label: string;
  start: string;
  opened: number;
  closed: number;
  open: number;
}

export function IssueBoards({
  points,
  bands,
  selectedId,
  onSelect,
}: {
  points: readonly InkIssuePoint[];
  bands: readonly InkAgingBand[];
  selectedId?: string;
  onSelect?: (weekId: string) => void;
}) {
  const styles = useStyles();
  if (points.length === 0) return null;
  const found = points.findIndex((point) => point.weekId === selectedId);
  const point = points[found >= 0 ? found : points.length - 1];
  const max = Math.max(1, ...points.map((item) => Math.max(item.opened, item.closed)));
  const yMax = Math.max(4, Math.ceil((max + 1) / 2) * 2);
  const yTicks = Array.from({ length: yMax / 2 + 1 }, (_, index) => index * 2);
  const openCount = bands.reduce((sum, band) => sum + band.count, 0);
  const table: VisualTable = {
    ariaLabel: "이슈 누적",
    columns: [
      { key: "week", label: "주" },
      { key: "opened", label: "등록", numeric: true },
      { key: "closed", label: "해결", numeric: true },
      { key: "open", label: "미결", numeric: true },
    ],
    rows: points.map((item) => ({
      key: item.weekId,
      cells: [item.label, item.opened, item.closed, item.open],
      onActivate: onSelect ? () => onSelect(item.weekId) : undefined,
      activateLabel: "그 주 보고 보기",
    })),
  };
  return (
    <div className={styles.pairWrap}>
      <div className={styles.pair}>
        <ReportVisual
          compact
          title="이슈 누적"
          description="실선은 등록 누적, 점선은 해결 누적입니다. 간격이 미결입니다."
          meta={point ? `${point.label} · 미결 ${point.open}건` : undefined}
          table={table}
          height={188}
          chart={(height) => (
            <TrendLineVisual
              height={height}
              ariaLabel="이슈 등록과 해결 누적"
              labels={points.map((item) => formatDate(item.start, "short"))}
              calloutTitles={points.map((item) => item.label)}
              yMin={0}
              yMax={yMax}
              yTicks={yTicks}
              formatY={(value) => `${value}`}
              onPointClick={onSelect ? (pointIndex) => onSelect(points[pointIndex]?.weekId ?? "") : undefined}
              series={[
                {
                  key: "opened",
                  legend: "등록",
                  color: tokens.colorNeutralForeground1,
                  values: points.map((item) => item.opened),
                  valueLabels: points.map((item) => `${item.opened}건`),
                },
                {
                  key: "closed",
                  legend: "해결",
                  color: tokens.colorNeutralForeground3,
                  values: points.map((item) => item.closed),
                  valueLabels: points.map((item) => `${item.closed}건`),
                  dashed: true,
                },
              ]}
            />
          )}
        />
        <ReportVisual compact title="미결의 나이" description="연 날부터 기준일까지입니다. 막대 길이는 건수 비율입니다." meta={`미결 ${openCount}건`}>
          <AgingList bands={bands} />
        </ReportVisual>
      </div>
    </div>
  );
}
